import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import {
  Database, Download, RefreshCw, CheckCircle2, XCircle, Clock,
  Archive, Play, RotateCcw, AlertTriangle, Trash2, Shield, Users
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatBytes = (bytes) => {
  if (!bytes || bytes < 100) return "< 1 KB";
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const formatDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const shortenId = (id) => id ? `${id.slice(0, 8)}...` : "—";

// Config dei tipi di backup globali (piattaforma)
const GLOBAL_SCOPES = {
  "_platform": {
    label: "Piattaforma Yollo (SuperAdmin)",
    shortLabel: "SuperAdmin & Config",
    icon: Shield,
    color: "violet",
    desc: "Aziende, abbonamenti, pacchetti, prezzi, codici sconto, superadmin",
    triggerParam: "tipo=platform",
    badgeClass: "bg-violet-100 text-violet-700 border-violet-200",
    iconBg: "bg-violet-100",
    iconColor: "text-violet-600",
    btnClass: "border-violet-200 text-violet-700 hover:bg-violet-50",
  },
  "_venditori": {
    label: "Venditori & Provvigioni",
    shortLabel: "Venditori",
    icon: Users,
    color: "emerald",
    desc: "Venditori, pagamenti provvigioni, storico provvigioni, tecnici",
    triggerParam: "tipo=venditori",
    badgeClass: "bg-emerald-100 text-emerald-700 border-emerald-200",
    iconBg: "bg-emerald-100",
    iconColor: "text-emerald-600",
    btnClass: "border-emerald-200 text-emerald-700 hover:bg-emerald-50",
  },
};

const BackupPanel = () => {
  const token = localStorage.getItem("sa_token");
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [restoring, setRestoring] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [confermaRestore, setConfermaRestore] = useState(null);
  const [confermaDelete, setConfermaDelete] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchBackups = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/backup/list`, { headers });
      setBackups(res.data || []);
    } catch { toast.error("Errore caricamento backup"); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchBackups(); }, [fetchBackups]);

  const triggerAll = async () => {
    setTriggering("all");
    try {
      await axios.post(`${API}/admin/backup/trigger`, null, { headers });
      toast.success("Backup avviato in background — ricarica tra qualche minuto");
      setTimeout(fetchBackups, 10000);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setTriggering(null); }
  };

  const triggerScope = async (scopeId, triggerParam) => {
    setTriggering(scopeId);
    try {
      const res = await axios.post(`${API}/admin/backup/trigger?${triggerParam}`, null, { headers });
      toast.success(`Backup completato: ${res.data.total_docs} documenti, ${res.data.files_copied ?? 0} file, ${formatBytes(res.data.size_bytes)}`);
      fetchBackups();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore backup"); }
    finally { setTriggering(null); }
  };

  const triggerSocieta = async (societa_id) => {
    setTriggering(societa_id);
    try {
      const res = await axios.post(`${API}/admin/backup/trigger?societa_id=${societa_id}`, null, { headers });
      toast.success(`Backup completato: ${res.data.total_docs} documenti, ${res.data.files_copied ?? 0} file, ${formatBytes(res.data.size_bytes)}`);
      fetchBackups();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore backup"); }
    finally { setTriggering(null); }
  };

  const downloadBackup = async (backup) => {
    setDownloading(backup.id);
    try {
      const res = await axios.get(`${API}/admin/backup/${backup.id}/download`, { headers, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `backup_${backup.societa_nome || backup.societa_id}_${backup.data}.json.gz`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Errore download"); }
    finally { setDownloading(null); }
  };

  const eseguiRestore = async (backup) => {
    setConfermaRestore(null);
    setRestoring(backup.id);
    try {
      const res = await axios.post(`${API}/admin/backup/${backup.id}/restore`, null, { headers });
      toast.success(`Ripristino completato: ${res.data.total_docs_ripristinati} documenti ripristinati`);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore ripristino"); }
    finally { setRestoring(null); }
  };

  const eseguiDelete = async (conf) => {
    setConfermaDelete(null);
    if (conf.type === "single") {
      setDeleting(conf.backup.id);
      try {
        await axios.delete(`${API}/admin/backup/${conf.backup.id}`, { headers });
        toast.success("Backup eliminato");
        setBackups(prev => prev.filter(b => b.id !== conf.backup.id));
      } catch (e) {
        if (e.response?.status === 404) {
          setBackups(prev => prev.filter(b => b.id !== conf.backup.id));
          toast.info("Backup già eliminato, lista aggiornata");
        } else {
          toast.error(e.response?.data?.detail || "Errore eliminazione");
        }
      }
      finally { setDeleting(null); }
    } else {
      setDeleting(`all_${conf.societa_id}`);
      try {
        const res = await axios.delete(`${API}/admin/backup/societa/${conf.societa_id}`, { headers });
        toast.success(`${res.data.deleted_count} backup eliminati per ${conf.nome}`);
        setBackups(prev => prev.filter(b => b.societa_id !== conf.societa_id));
      } catch (e) {
        if (e.response?.status === 404) {
          setBackups(prev => prev.filter(b => b.societa_id !== conf.societa_id));
          toast.info("Backup già eliminati, lista aggiornata");
        } else {
          toast.error(e.response?.data?.detail || "Errore eliminazione");
        }
      }
      finally { setDeleting(null); }
    }
  };

  // Separa backup globali (piattaforma) dai backup clienti
  const globalBackups = backups.filter(b => b.societa_id?.startsWith("_"));
  const clientBackups = backups.filter(b => !b.societa_id?.startsWith("_"));

  // Raggruppa clienti per società
  const grouped = clientBackups.reduce((acc, b) => {
    const key = b.societa_id;
    if (!acc[key]) acc[key] = { nome: b.societa_nome || shortenId(b.societa_id), backups: [] };
    acc[key].backups.push(b);
    return acc;
  }, {});

  // Raggruppa backup globali per scope
  const groupedGlobal = globalBackups.reduce((acc, b) => {
    const key = b.societa_id;
    if (!acc[key]) acc[key] = [];
    acc[key].push(b);
    return acc;
  }, {});

  const renderBackupRow = (backup, showRestore = true) => (
    <div key={backup.id} className="flex items-center justify-between px-5 py-3" data-testid={`backup-row-${backup.id}`}>
      <div className="flex items-center gap-3">
        {backup.status === "success"
          ? <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
          : <XCircle className="h-4 w-4 text-red-500 shrink-0" />}
        <div>
          <p className="text-sm font-medium text-gray-800">{backup.data}</p>
          <div className="flex items-center gap-3 mt-0.5 flex-wrap">
            {backup.status === "success" ? (
              <>
                <span className="text-xs text-gray-400 flex items-center gap-1">
                  <Archive className="h-3 w-3" />{formatBytes(backup.size_bytes)}
                </span>
                <span className="text-xs text-gray-400">{backup.total_docs?.toLocaleString()} doc · {backup.files_copied ?? 0} file</span>
                <span className="text-xs text-gray-400 flex items-center gap-1">
                  <Clock className="h-3 w-3" />{formatDate(backup.created_at)}
                </span>
              </>
            ) : (
              <span className="text-xs text-red-500">{backup.error?.slice(0, 80)}</span>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1.5">
        {backup.archiviato && <Badge variant="outline" className="text-[10px] text-gray-400">Archiviato</Badge>}
        {backup.status === "success" && !backup.archiviato && (
          <>
            <Button variant="outline" size="sm" onClick={() => downloadBackup(backup)}
              disabled={!!downloading} className="rounded-xl text-xs h-8" data-testid={`btn-download-${backup.id}`}>
              <Download className={`h-3.5 w-3.5 mr-1.5 ${downloading === backup.id ? "animate-bounce" : ""}`} />
              {downloading === backup.id ? "..." : "Download"}
            </Button>
            {showRestore && (
              <Button size="sm" variant="outline"
                className="rounded-xl text-xs h-8 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                onClick={() => setConfermaRestore(backup)}
                disabled={!!restoring}
                data-testid={`btn-restore-${backup.id}`}>
                <RotateCcw className={`h-3.5 w-3.5 mr-1.5 ${restoring === backup.id ? "animate-spin" : ""}`} />
                {restoring === backup.id ? "..." : "Ripristina"}
              </Button>
            )}
          </>
        )}
        <Button size="sm" variant="ghost"
          className="rounded-xl h-8 w-8 p-0 text-gray-400 hover:text-red-500 hover:bg-red-50"
          onClick={() => setConfermaDelete({ type: "single", backup })}
          disabled={deleting === backup.id}
          data-testid={`btn-delete-${backup.id}`}
          title="Elimina questo backup">
          {deleting === backup.id
            ? <RefreshCw className="h-3.5 w-3.5 animate-spin" />
            : <Trash2 className="h-3.5 w-3.5" />}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">

      {/* Dialog conferma ripristino */}
      <Dialog open={!!confermaRestore} onOpenChange={(o) => !o && setConfermaRestore(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />Conferma ripristino
            </DialogTitle>
            <DialogDescription>
              Stai per ripristinare <strong>{confermaRestore?.societa_nome || "questa società"}</strong> al backup del <strong>{confermaRestore?.data}</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-red-50 rounded-xl p-3 text-sm text-red-700 border border-red-100">
            Questa operazione <strong>sovrascrive tutti i dati attuali</strong> con quelli del backup selezionato. Non reversibile.
          </div>
          <DialogFooter className="flex gap-2 mt-2">
            <Button variant="outline" className="flex-1" onClick={() => setConfermaRestore(null)}>Annulla</Button>
            <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              onClick={() => eseguiRestore(confermaRestore)} data-testid="btn-conferma-restore">
              <RotateCcw className="h-4 w-4 mr-1.5" />Sì, ripristina
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog conferma eliminazione */}
      <Dialog open={!!confermaDelete} onOpenChange={(o) => !o && setConfermaDelete(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-800">
              <Trash2 className="h-5 w-5 text-red-500" />
              {confermaDelete?.type === "all" ? "Elimina tutti i backup" : "Elimina backup"}
            </DialogTitle>
            <DialogDescription>
              {confermaDelete?.type === "all"
                ? <>Elimini tutti i backup di <strong>{confermaDelete?.nome}</strong>. Non reversibile.</>
                : <>Elimini il backup del <strong>{confermaDelete?.backup?.data}</strong> di <strong>{confermaDelete?.backup?.societa_nome || "questa voce"}</strong>.</>
              }
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 mt-2">
            <Button variant="outline" className="flex-1" onClick={() => setConfermaDelete(null)}>Annulla</Button>
            <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              onClick={() => eseguiDelete(confermaDelete)} data-testid="btn-conferma-delete">
              <Trash2 className="h-4 w-4 mr-1.5" />Elimina
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Database className="h-5 w-5 text-indigo-600" />Backup Automatici
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Backup notturno alle 04:00 · retention 30 giorni · Emergent Object Storage
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchBackups} disabled={loading} className="rounded-xl">
            <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />Aggiorna
          </Button>
          <Button size="sm" onClick={triggerAll} disabled={!!triggering}
            className="rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="btn-backup-all">
            <Play className="h-4 w-4 mr-1.5" />
            {triggering === "all" ? "In corso..." : "Backup ora (tutti)"}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-3 opacity-50" />Caricamento...
        </div>
      ) : (
        <div className="space-y-6">

          {/* ── SEZIONE PIATTAFORMA ── */}
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-1">
              Backup Piattaforma
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {Object.entries(GLOBAL_SCOPES).map(([scopeId, cfg]) => {
                const Icon = cfg.icon;
                const scopeBackups = groupedGlobal[scopeId] || [];
                const latest = scopeBackups[0];
                return (
                  <div key={scopeId} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                    {/* Header scope */}
                    <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 bg-gray-50">
                      <div className="flex items-center gap-2.5">
                        <div className={`w-8 h-8 rounded-xl ${cfg.iconBg} flex items-center justify-center`}>
                          <Icon className={`h-4 w-4 ${cfg.iconColor}`} />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900 text-sm">{cfg.shortLabel}</p>
                          <p className="text-xs text-gray-400">
                            {scopeBackups.length > 0
                              ? `${scopeBackups.length} backup · ultimo: ${latest?.data}`
                              : "Nessun backup ancora"}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Button variant="outline" size="sm"
                          onClick={() => triggerScope(scopeId, cfg.triggerParam)}
                          disabled={!!triggering}
                          className={`rounded-xl text-xs ${cfg.btnClass}`}
                          data-testid={`btn-backup-${scopeId}`}>
                          {triggering === scopeId
                            ? <><RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" />In corso...</>
                            : <><Play className="h-3.5 w-3.5 mr-1.5" />Backup ora</>}
                        </Button>
                        {scopeBackups.length > 0 && (
                          <Button variant="outline" size="sm"
                            className="rounded-xl text-xs text-red-500 hover:text-red-600 hover:bg-red-50 border-red-200"
                            onClick={() => setConfermaDelete({ type: "all", societa_id: scopeId, nome: cfg.shortLabel })}
                            disabled={deleting === `all_${scopeId}`}
                            data-testid={`btn-delete-all-${scopeId}`}>
                            {deleting === `all_${scopeId}`
                              ? <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                              : <Trash2 className="h-3.5 w-3.5" />}
                          </Button>
                        )}
                      </div>
                    </div>
                    {/* Descrizione */}
                    <div className="px-5 py-2 border-b border-gray-50">
                      <p className="text-xs text-gray-400">{cfg.desc}</p>
                    </div>
                    {/* Lista backup */}
                    {scopeBackups.length === 0 ? (
                      <div className="px-5 py-4 text-sm text-gray-400 text-center">
                        Nessun backup — premi "Backup ora" per crearne uno
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-50">
                        {scopeBackups.map(b => renderBackupRow(b, true))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── SEZIONE CLIENTI ── */}
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-1">
              Backup Clienti ({Object.keys(grouped).length} aziende)
            </p>
            {Object.keys(grouped).length === 0 ? (
              <div className="text-center py-16 text-gray-400 bg-white rounded-2xl border border-gray-200">
                <Database className="h-12 w-12 mx-auto mb-4 opacity-30" />
                <p className="font-medium">Nessun backup clienti disponibile</p>
                <p className="text-sm mt-1">Il primo backup verrà eseguito stanotte alle 04:00</p>
                <Button onClick={triggerAll} disabled={!!triggering} className="mt-4 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white">
                  <Play className="h-4 w-4 mr-2" />Esegui backup ora
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {Object.entries(grouped).map(([societa_id, { nome, backups: sb }]) => (
                  <div key={societa_id} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                    <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 bg-gray-50">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-xl bg-indigo-100 flex items-center justify-center">
                          <Database className="h-4 w-4 text-indigo-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900 text-sm">{nome}</p>
                          <p className="text-xs text-gray-400">{sb.length} backup · ultimo: {sb[0]?.data}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => triggerSocieta(societa_id)}
                          disabled={!!triggering} className="rounded-xl text-xs" data-testid={`btn-backup-${societa_id}`}>
                          {triggering === societa_id
                            ? <><RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" />In corso...</>
                            : <><Play className="h-3.5 w-3.5 mr-1.5" />Backup ora</>}
                        </Button>
                        <Button variant="outline" size="sm"
                          className="rounded-xl text-xs text-red-500 hover:text-red-600 hover:bg-red-50 border-red-200"
                          onClick={() => setConfermaDelete({ type: "all", societa_id, nome })}
                          disabled={deleting === `all_${societa_id}`}
                          data-testid={`btn-delete-all-${societa_id}`}
                          title="Elimina tutti i backup di questa società">
                          {deleting === `all_${societa_id}`
                            ? <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                            : <Trash2 className="h-3.5 w-3.5" />}
                        </Button>
                      </div>
                    </div>
                    <div className="divide-y divide-gray-50">
                      {sb.map(b => renderBackupRow(b, true))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      )}
    </div>
  );
};

export default BackupPanel;
